import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Instagram } from 'lucide-react';
import PrintButton from '../components/PrintButton';
import SEO from '../components/SEO';

/* An editorial-style long-form essay page. Kept intentionally
 * different from the rest of the app: a single narrow reading
 * column, serif prose, no cards or gradients. This is Angela's
 * voice, and the visual should feel closer to a printed piece
 * than a product page. */

export default function Why() {
  return (
    <div className="tf-essay-page py-12 sm:py-16" data-testid="why-screen">
      <SEO
        title="Information, autonomy and informed choice — why I created The Full Impact"
        description="Angela Moreton on why survivors should be given clear, written information after a disclosure — so that they can return to it privately, at their own pace, and make informed choices."
        path="/app/why"
      />

      <div className="max-w-[640px] mx-auto px-5 sm:px-6">
        <p
          className="font-body font-bold"
          data-testid="why-eyebrow"
          style={{
            fontSize: 10.5,
            letterSpacing: '0.28em',
            color: 'var(--tf-purple)',
          }}
        >
          A NOTE FROM ANGELA
        </p>

        <h1
          className="font-heading mt-4"
          data-testid="why-title"
          style={{
            fontSize: 'clamp(30px, 5vw, 44px)',
            lineHeight: 1.12,
            letterSpacing: '-0.015em',
            color: 'var(--tf-text)',
          }}
        >
          Information, autonomy and informed choice.
        </h1>

        <p
          className="font-body mt-6"
          style={{
            fontSize: 15,
            lineHeight: 1.55,
            color: 'var(--tf-text-secondary)',
          }}
        >
          Why I created The Full Impact.
        </p>

        <div className="mt-6 mb-10">
          <PrintButton label="Print / Save this piece" />
        </div>

        <article
          data-testid="why-article"
          className="font-body tf-essay"
          style={{
            fontSize: 17,
            lineHeight: 1.75,
            color: 'var(--tf-text)',
            fontFamily: 'Fraunces, Georgia, serif',
            fontWeight: 400,
          }}
        >
          <p>
            One of the main reasons I created The Full Impact was because of my own
            experience of approaching the police.
          </p>

          <p>
            Initially, I was not looking to make a formal report. I wanted somebody to
            explain the process to me. I wanted to understand what would happen, what
            my options were, what my rights were and what support would be available
            so that I could take everything in and make an informed decision.
          </p>

          <p>
            Once I began explaining what had happened, however, the situation moved
            very quickly and much of it felt as though it had been taken out of my
            hands.
          </p>

          <p>
            I was not given one clear written source of information that I could take
            away and read in my own time. There was no simple leaflet or email
            bringing together the process, my rights, support, trauma responses and
            what might happen next.
          </p>

          <blockquote className="tf-pullquote">
            For somebody dealing with trauma, that matters.
          </blockquote>

          <p>
            A disclosure or report can be an extremely stressful experience. People
            may be frightened, overwhelmed, dissociating or struggling to remember
            what they have been told. Information given verbally during one
            conversation may simply be too much to absorb.
          </p>

          <p>
            That is why I believe survivors should routinely be given clear written
            information after a disclosure or first contact — whether through a
            leaflet, email, QR code or link to a trusted resource such as The Full
            Impact website.
          </p>

          <p>
            The survivor could then return to that information privately, at their
            own pace and as many times as they need.
          </p>

          <p>It could explain:</p>

          <ul className="tf-essay-list">
            <li>what may happen next;</li>
            <li>what choices and rights the survivor has;</li>
            <li>what the police and other organisations may need to do and why;</li>
            <li>what an ISVA or IDVA is and how independent advocacy can help;</li>
            <li>where specialist support is available;</li>
            <li>
              common trauma responses and how trauma can affect memory, communication
              and decision-making;
            </li>
            <li>what happens if somebody is not ready to report;</li>
            <li>what to expect during an investigation or justice process;</li>
            <li>and where to go if they have further questions.</li>
          </ul>

          <p>
            Providing information does not mean replacing professional advice or
            preventing organisations from carrying out safeguarding or legal
            responsibilities.
          </p>

          <p>
            It means making sure that, as far as possible, the survivor understands
            what is happening around them.
          </p>

          <p>
            For me, trauma-informed practice should include giving people information
            in a form they can take away, process and return to when they are ready.
          </p>

          <blockquote className="tf-pullquote">
            A survivor should not have to remember everything they were told during
            one of the most stressful conversations of their life.
          </blockquote>

          <p className="tf-essay-signoff">That is why I created The Full Impact.</p>

          <p className="tf-essay-signature">
            <span>Angela Moreton</span>
            <span
              className="font-body"
              style={{
                fontSize: 12,
                letterSpacing: '0.18em',
                color: 'var(--tf-purple)',
                display: 'block',
                marginTop: 4,
                fontWeight: 700,
              }}
            >
              THE FULL IMPACT
            </span>
          </p>
        </article>

        {/* Onward links */}
        <div
          className="mt-14 pt-10"
          style={{ borderTop: '1px solid var(--tf-card-border)' }}
        >
          <p className="eyebrow">WHERE TO NEXT</p>
          <div className="mt-5 flex flex-col gap-3">
            <Link
              to="/app/sexual-violence"
              data-testid="why-cta-sv"
              className="inline-flex items-center justify-between gap-3 py-3 px-4 rounded-2xl"
              style={{
                background: 'var(--tf-bg-secondary)',
                color: 'var(--tf-text)',
                textDecoration: 'none',
                border: '1px solid var(--tf-card-border)',
              }}
            >
              <span className="font-body font-bold" style={{ fontSize: 14 }}>
                Sexual violence — the calm guide
              </span>
              <ArrowRight size={16} color="var(--tf-purple)" />
            </Link>
            <Link
              to="/app/domestic-abuse"
              data-testid="why-cta-da"
              className="inline-flex items-center justify-between gap-3 py-3 px-4 rounded-2xl"
              style={{
                background: 'var(--tf-bg-secondary)',
                color: 'var(--tf-text)',
                textDecoration: 'none',
                border: '1px solid var(--tf-card-border)',
              }}
            >
              <span className="font-body font-bold" style={{ fontSize: 14 }}>
                Domestic abuse — the calm guide
              </span>
              <ArrowRight size={16} color="var(--tf-purple)" />
            </Link>
            <Link
              to="/app/about"
              data-testid="why-cta-about"
              className="inline-flex items-center justify-between gap-3 py-3 px-4 rounded-2xl"
              style={{
                background: 'var(--tf-bg-secondary)',
                color: 'var(--tf-text)',
                textDecoration: 'none',
                border: '1px solid var(--tf-card-border)',
              }}
            >
              <span className="font-body font-bold" style={{ fontSize: 14 }}>
                About, privacy &amp; contact
              </span>
              <ArrowRight size={16} color="var(--tf-purple)" />
            </Link>
            <a
              href="https://www.instagram.com/thefullimpactcampaign"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="why-cta-instagram"
              className="inline-flex items-center justify-between gap-3 py-3 px-4 rounded-2xl"
              style={{
                background: 'var(--tf-bg-secondary)',
                color: 'var(--tf-text)',
                textDecoration: 'none',
                border: '1px solid var(--tf-card-border)',
              }}
            >
              <span className="font-body font-bold inline-flex items-center gap-2" style={{ fontSize: 14 }}>
                <Instagram size={15} />
                Follow on Instagram
              </span>
              <ArrowRight size={16} color="var(--tf-purple)" />
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
